import { Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  asSpelled,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  DataTable,
  FilterBox,
  HelpPopover,
  PageHeader,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type ICompactMenuItem,
  type IDataColumn,
  WorkControls,
  openRow,
  PaneControl,
  RowOpens,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import type { IPage } from '../../api/oversight-contracts';
import type { IRecordFacets, RecordListSortKey } from '../../api/participant-contracts';
import { LoadError, PastTheEnd, useAdminData } from '../../components/admin-page';
import { useQueueSearch } from '../compliance/queue-search';
import {
  RECORD_AREA_LABEL,
  recordPathIn,
  type ICardLinkState
} from '../../components/participant-link';
import {
  cycleCodesOf,
  filterNamed,
  optionLabel,
  RECORD_ACCOUNT_STATES,
  SEARCH_RULE,
  useCatalogue,
  type IFilterOption
} from '../../components/record-filters';
import {
  RETIRED_FILTERS,
  RetiredFilterChips,
  retiredFilterChips
} from '../../components/retired-filters';
import { RowNameClicks } from '../../components/row-name-click';
import { SearchScopes } from '../../components/search-scopes';
import { StateCards } from '../../components/state-cards';
import { refusalMessage } from '../../refusal';
import { FunnelHeader, ParamFunnel, SortHeader } from '../compliance/queue-headers';
import { useOwnColumns } from '../lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import {
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from '../lists/work-page/pager';
import { ParamChip } from '../sec-adv/firm-filters';
import {
  RecordListArea,
  type IFilterContext,
  type IRecordListSpec,
  type IRecordRow
} from './record-list';

/**
 * Teams and Firms as work pages (SR-514; `docs/superpowers/specs/2026-10-28-work-pages-design.md`)
 * of the work-item-backed family: each list's own route, whose every filter, card and sort is a
 * query parameter, the headers' funnels and the filter line's chips written here over those keys.
 * Firms is the light form — no cards, no counts read; Teams the segmented one — view cards with
 * counts and no signals, read once under the search and the filters (`GET /admin/teams/counts`),
 * never the card, the sort or the window.
 *
 * **The search reads as typed**, a quarter-second after the last key, replacing the address — the
 * register's way, where `record-list.tsx`'s submitted box was People's. **The list is the URL**:
 * `q`, `card`, every filter, `sort` and the window (`page`, `size`); Per page, the columns and the
 * card size are the viewer's, on this browser. A filter's change drops the window (`dropPaging`).
 *
 * **A funnel is single-valued**, as survey-api's parameters are: it lists the column's values
 * among the rows the other filters keep, with their counts (`…/facets`), and where that read fails
 * the field's known vocabulary uncounted. The record pane beside the list stays `record-list.tsx`'s
 * (`RecordListArea`), handed a read that knows this page's keys and window.
 */

/** survey-api's page ceiling on Teams and Firms. */
const RECORDS_MAX_PAGE = 100;

/** One funnel's parameter: the URL key — also the facets read's `field` — and its vocabulary;
 *  `'cycles'` is the catalogue's cycle codes, typed into as a prefix. */
export interface IFacetGroup {
  param: string;
  label: string;
  known: readonly IFilterOption[] | 'cycles';
}

/** A column's funnel: the column it heads and the parameters it sets, one choice each. */
export interface IColumnFilter {
  column: string;
  groups: readonly IFacetGroup[];
}

/** A view card: its `card=` key (`all` is no key), its words and the "?"'s line. */
export interface IRecordCard {
  key: string;
  label: string;
  hint: string;
  about: string;
}

/** A counts read's answer, as `/teams/counts` sends it. */
export interface IRecordCounts {
  cards: Partial<Record<string, { count: number }>>;
}

export interface IRecordWorkSpec<Row extends IRecordRow, Query> {
  area: 'teams' | 'firms';
  /** The record in words, one and many — "team", "teams". */
  counted: [string, string];
  placeholder: string;
  /** The "?"'s opening — what the list holds. */
  about: React.ReactNode;
  /** The URL's own filter keys past `cycle`, `roster` and the retired ones. */
  filters: readonly string[];
  queryOf: (params: URLSearchParams, page: number, pageSize: number) => Query;
  list: (query: Query, signal: AbortSignal) => Promise<IPage<Row>>;
  facets: (
    field: string,
    query: Query,
    prefix: string,
    signal: AbortSignal
  ) => Promise<IRecordFacets>;
  /** The page's columns, the record's name first. */
  columns: (search: string, q: string) => IDataColumn<Row>[];
  /** The columns whose label sorts, by id: survey-api's key and its first order. */
  sorts: Readonly<Record<string, { key: RecordListSortKey; first: 'asc' | 'desc' }>>;
  funnels: readonly IColumnFilter[];
  /** The toolbar's own quick filters. */
  quick?: (context: IFilterContext) => React.ReactNode;
  /** The quick filters set, as chips of the applied filters' row — each opening its control with
   *  `open`, by the control's name. */
  quickChips?: (context: IFilterContext, open: (label: string) => true) => ICompactChip[];
  /** The segmented form's view cards and their counts read. */
  cards?: {
    list: readonly IRecordCard[];
    counts: (query: Query, signal: AbortSignal) => Promise<IRecordCounts>;
  };
  /** The pane's line under a record's name. */
  secondary: (row: Row) => string;
}

/** The list's own keys that say where the reader is rather than what narrows: Clear all keeps
 *  the card and the order. */
const KEPT = ['card', 'sort'];

export function RecordWorkPage<Row extends IRecordRow, Query>({
  spec
}: {
  spec: IRecordWorkSpec<Row, Query>;
}) {
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const title = RECORD_AREA_LABEL[spec.area];
  const [one, many] = spec.counted;
  const q = params.get('q') ?? '';
  const card = params.get('card');
  const sort = params.get('sort');

  const catalogue = useCatalogue();
  const cycles = React.useMemo(
    () => cycleCodesOf(catalogue, []).map((code) => ({ value: code, label: code })),
    [catalogue]
  );

  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const query = spec.queryOf(params, page, perPage);
  const asked = JSON.stringify([query, pages]);
  const filtered = ['cycle', 'roster', ...RETIRED_FILTERS, ...spec.filters].some((name) =>
    filterNamed(params, name)
  );
  const { data, error, reading, reload } = useAdminData<IAnswered<Row>>(
    async (signal) => {
      try {
        return {
          asked,
          q,
          searched: q !== '',
          filtered,
          card,
          page: await readItemPages(
            (at, pageSize) => spec.list(spec.queryOf(params, at, pageSize), signal),
            page,
            perPage,
            pages,
            RECORDS_MAX_PAGE
          )
        };
      } catch (listError) {
        // survey-api's `problems[]` say which parameter is wrong and why; its title does not.
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed. An error
  // stands only while no newer read is out.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const readAt = useReadAt(data);

  // The counts' query: the search and the filters, never the card, the sort or the window.
  const countsAsked = countsKeyOf(params);
  const counts = useAdminData<Nullable<IRecordCounts>>(
    (signal) => (spec.cards ? spec.cards.counts(query, signal) : Promise.resolve(null)),
    [countsAsked]
  );
  // A count survey-api did not just say is not drawn: a failed read leaves "…".
  const counted = counts.error ? null : counts.data;

  /** A change of search or filter starts at the top — made on the address as it is when it
   *  lands, so the search's delayed write never undoes a filter set meanwhile. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  // Sending the query already on screen asks again: a record made a moment ago is found.
  const search = (text: string) => {
    if (text === q) refresh.onClick();
    else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  };
  const choose = (name: string) => (value: Nullable<string>) =>
    update((next) => (value === null || value === '' ? next.delete(name) : next.set(name, value)));
  const context: IFilterContext = { params, choose };

  const sortKey = sort === null ? null : sort.replace(/:desc$/, '');
  const descending = sort?.endsWith(':desc') ?? false;
  // A press on the order in force turns it round; on another column, sorts by it its first way.
  const sortBy = (key: RecordListSortKey, first: 'asc' | 'desc') =>
    update((next) => {
      const dir = sortKey === key ? (descending ? 'asc' : 'desc') : first;
      next.set('sort', dir === 'desc' ? `${key}:desc` : key);
    });

  const clearAll = () =>
    setParams((current) => {
      const next = new URLSearchParams();
      for (const name of KEPT) {
        const value = current.get(name);
        if (value !== null) next.set(name, value);
      }
      return next;
    });

  // The funnels, held here so a filter chip's press opens its column's.
  const [funnelOpen, setFunnelOpen] = React.useState<Nullable<string>>(null);
  const knownOf = (group: IFacetGroup) => (group.known === 'cycles' ? cycles : group.known);
  const saidOf = (group: IFacetGroup): Nullable<string> =>
    optionLabel(knownOf(group), params.get(group.param));
  const funnelBody = (filter: IColumnFilter) =>
    filter.groups.map((group) => (
      <FacetChoice
        key={group.param}
        label={group.label}
        heading={filter.groups.length > 1}
        typed={group.known === 'cycles'}
        base={JSON.stringify([group.param, { ...query, page: undefined, pageSize: undefined }])}
        read={(prefix, signal) => spec.facets(group.param, query, prefix, signal)}
        value={params.get(group.param)}
        known={knownOf(group)}
        onChoose={choose(group.param)}
      />
    ));
  const funnelOf = (filter: IColumnFilter, label: string) => {
    const said = filter.groups.flatMap((group) => saidOf(group) ?? []).join(' · ');
    return (
      <ParamFunnel
        code={filter.column}
        label={label}
        said={said || null}
        open={funnelOpen === filter.column}
        onOpenChange={(open) => setFunnelOpen(open ? filter.column : null)}
      >
        {funnelBody(filter)}
      </ParamFunnel>
    );
  };

  const own = spec.columns(location.search, shown?.q ?? '').map((column) => {
    const label = column.label ?? (typeof column.header === 'string' ? column.header : column.id);
    const sorted = spec.sorts[column.id];
    const filter = spec.funnels.find((entry) => entry.column === column.id);
    const funnel = filter && funnelOf(filter, label);
    // The Created column is offered, not drawn, until the viewer adds it.
    const hidden = column.optional ? true : column.hidden;
    if (sorted) {
      const active = sortKey === sorted.key;
      const direction = active ? (descending ? 'desc' : 'asc') : sorted.first;
      return {
        ...column,
        hidden,
        label,
        header: (
          <SortHeader
            label={label}
            direction={direction}
            active={active}
            note={`${active ? 'Sorted' : 'Sort'} ${orderWords(sorted.key, direction)}`}
            onPress={() => sortBy(sorted.key, sorted.first)}
            funnel={funnel}
          />
        )
      };
    }
    if (funnel) {
      return { ...column, hidden, label, header: <FunnelHeader label={label} funnel={funnel} /> };
    }
    return { ...column, hidden };
  });
  const chosen = useOwnColumns<Row>(`records.${spec.area}`);
  // A record beside the list (the row's first icon), or by *Opens in* (a press on the row).
  const prefs = usePanePrefs();
  const beside = (row: Row) =>
    openRecord(recordPathIn(spec.area, row.participantId), { search: location.search, list: true } satisfies ICardLinkState);
  const open = (row: Row) =>
    openRow(prefs, recordPathIn(spec.area, row.participantId), () => beside(row));
  // The ways in first on a row — beside the list, and ↗ its page in a new tab; the column is none
  // the viewer arranges or hides.
  const columns: IDataColumn<Row>[] = [
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (row) => (
        <RowOpens
          name={row.displayName}
          onPane={() => beside(row)}
          to={recordPathIn(spec.area, row.participantId)}
          tabWords={`Open ${row.displayName} in a new tab`}
        />
      )
    },
    ...chosen.resolve(own)
  ];
  const columnsButton = chosen.button(own);

  // The card the empty answer is of, All being no narrowing.
  const cardLabel =
    shown?.card && shown.card !== 'all'
      ? (spec.cards?.list.find((entry) => entry.key === shown.card)?.label ?? asSpelled(shown.card))
      : null;
  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={spec.counted}
                  onPage={paging.setPage}
                />
              ) : (
                emptyStatement(one, shown.searched, shown.filtered, cardLabel)
              )
          }
        : undefined;

  // The header's number is All's: on a page with cards, survey-api's count under the search and
  // the filters — unread, the list's own total where it is All, of this query and not the last;
  // without cards, the list's total, which is its All.
  const listTotal = shown?.asked === asked ? shown.page.total : null;
  const total = spec.cards
    ? (counted?.cards.all?.count ?? ((card ?? 'all') === 'all' ? listTotal : null))
    : (shown?.page.total ?? null);
  const cards = spec.cards && <ViewCards cards={spec.cards.list} counted={counted} />;

  const lineEntries: ICompactChip[] = spec.funnels.flatMap((filter) =>
    filter.groups.flatMap((group) => {
      const said = saidOf(group);
      if (said === null) return [];
      return [
        {
          key: group.param,
          chip: (
            <ParamChip
              words={`${group.label}: ${said}`}
              body={funnelBody({ ...filter, groups: [group] })}
              onPress={() => {
                const drawn = document.querySelector(`[data-funnel="${CSS.escape(filter.column)}"]`);
                if (drawn === null) return false;
                setFunnelOpen(filter.column);
                return true;
              }}
              onRemove={() => choose(group.param)(null)}
            />
          )
        }
      ];
    })
  );

  // Every filter applied as a chip with its value — under the toolbar and on the compact bar's
  // second row; a quick filter's press opens its control (`open`), a
  // funnel's its column's.
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const toolbar = useWorkToolbar();
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const quickChipsWith = (open: (label: string) => true) => [
    ...(spec.quickChips?.(context, open) ?? []),
    ...retiredFilterChips(params, choose, RECORD_ACCOUNT_STATES, open)
  ];
  const filterChips = quickChipsWith(openFilters);
  const quickCount = filterChips.length;
  // Firms has no quick filter of its own: its *Filters* is drawn only while an old link's is.
  const quickOffered = spec.quick !== undefined || quickCount > 0;
  const appliedChips = [...filterChips, ...lineEntries];
  const pageChips = [...quickChipsWith(toolbar.openQuick), ...lineEntries];
  const cardItems = viewCardItems(spec.cards?.list ?? [], counted, params);
  const openItem = cardItems.find((item) => item.current);

  // The toolbar's quick filters, drawn in the page and again in the compact bar.
  const quickFilters = (
    <>
      {spec.quick?.(context)}
      <RetiredFilterChips
        params={params}
        choose={choose}
        accountStates={RECORD_ACCOUNT_STATES}
        name={spec.area}
      />
    </>
  );

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  const refresh = {
    reading: reading || counts.reading,
    readAt,
    onClick: () => {
      void reload();
      if (spec.cards) void counts.reload();
    }
  };
  const help = (
    <HelpPopover label={`About ${title}`} align="end">
      <p>{spec.about}</p>
      {spec.cards && (
        <ul className="grid list-disc gap-1 pl-5">
          {spec.cards.list.map((entry) => (
            <li key={entry.key}>
              <span className="font-medium">{entry.label}</span> — {entry.about}
            </li>
          ))}
        </ul>
      )}
      <p>The search reads as you type. {SEARCH_RULE}</p>
      <p>
        A row opens beside the list by its first icon and in a new tab by ↗; a click on the row or
        its name goes where «Opens in» says.
      </p>
      <p>
        A column&apos;s name sorts by it, a press again the other way; with none chosen a search
        ranks the best match first, else the newest come first. A column&apos;s funnel narrows the
        list to one of its values, counted among the {many} the other filters keep. Clear all keeps
        the {spec.cards ? 'card and the ' : ''}order.
      </p>
    </HelpPopover>
  );
  return (
    <>
      <PageHeader
        title={title}
        row
        actions={
          <WorkPageHeaderActions
            summary={
              total === null ? null : `${total.toLocaleString('en-US')} ${total === 1 ? one : many}`
            }
            summaryData={{ 'data-records-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label={`${title}, compact`}
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={
          appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />
        }
      >
        <CompactTitle>{title}</CompactTitle>
        {openItem && (
          <CompactMenu
            label="Show"
            chip
            trigger={
              <>
                {openItem.label}
                <span className="font-semibold tabular-nums">{openItem.count}</span>
              </>
            }
            items={cardItems}
          />
        )}
        <CompactDivider />
        <RecordSearch
          q={q}
          label={`Filter ${many}`}
          placeholder={`Filter ${many}…`}
          onSearch={search}
          compact
        />
        {quickOffered && (
          <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
            {quickFilters}
          </CompactFilters>
        )}
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {cards}

        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={
              <RecordSearch
                q={q}
                label={`Filter ${many}`}
                placeholder={`Filter ${many}…`}
                onSearch={search}
              />
            }
            quick={quickFilters}
            quickCount={quickCount}
            end={
              <>
                {columnsButton}
                <PaneControl prefs={prefs} kind="record" />
              </>
            }
          />
          {pageChips.length > 0 && (
            <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />
          )}
          <SearchScopes
            scope={spec.area}
            q={q}
            total={data?.asked === asked ? data.page.total : null}
            failed={failed && data?.asked !== asked}
          />
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}

        <RowNameClicks>
          <DataTable
            label={title}
            columns={columns}
            rows={tableState ? null : (shown?.page.items ?? null)}
            rowKey={(row) => row.participantId}
            rowProps={(row) =>
              ({ 'data-participant': row.participantId }) as React.HTMLAttributes<HTMLTableRowElement>
            }
            onOpen={open}
            busy={reading && shown !== null}
            state={tableState}
            minWidth="64rem"
            stickyTop={STICKY_TABLE_TOP}
          />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** A list and its records as one area: the list, or a record with the list beside it — the pane
 *  read under the list's own query builder and its window (`windowOf`), in the viewer's Per
 *  page. */
export function RecordWorkArea<Row extends IRecordRow, Query>({
  spec
}: {
  spec: IRecordWorkSpec<Row, Query>;
}) {
  const [perPage] = usePerPage();
  const listSpec = React.useMemo<IRecordListSpec<Row>>(
    () => ({
      area: spec.area,
      counted: spec.counted,
      placeholder: spec.placeholder,
      rule: spec.about,
      filters: spec.filters,
      columns: spec.columns,
      secondary: spec.secondary,
      read: (params, page, signal) => {
        const window = windowOf(params, perPage);
        return readItemPages(
          (at, pageSize) => spec.list(spec.queryOf(params, at, pageSize), signal),
          page,
          perPage,
          page === window.page ? window.pages : 1,
          RECORDS_MAX_PAGE
        );
      }
    }),
    [spec, perPage]
  );
  return <RecordListArea spec={listSpec} />;
}

/** What a counts read answers: the address less the card, the sort and the window — a change
 *  of one of those reads no counts. */
export function countsKeyOf(params: URLSearchParams): string {
  const kept = new URLSearchParams(params);
  for (const name of ['card', 'sort', 'page', 'size']) kept.delete(name);
  kept.sort();
  return kept.toString();
}

/** A segmented page's view cards (`card=`, All no key), compact — each its count as survey-api
 *  read it under the search and the filters, "…" until read or where the read failed. */
export function ViewCards({
  cards,
  counted,
  said = {}
}: {
  cards: readonly IRecordCard[];
  counted: Nullable<IRecordCounts>;
  /** A card's count the page says itself, by key — one the counts read does not count. */
  said?: Readonly<Record<string, string>>;
}) {
  const [params] = useSearchParams();
  const card = params.get('card') ?? 'all';
  const all = counted?.cards.all?.count ?? null;
  return (
    <StateCards
      label="Show"
      compact
      cards={cards.map((entry) => {
        const next = dropPaging(new URLSearchParams(params));
        if (entry.key === 'all') next.delete('card');
        else next.set('card', entry.key);
        const count = counted?.cards[entry.key]?.count ?? null;
        return {
          key: entry.key,
          to: { search: next.toString() },
          current: card === entry.key,
          label: entry.label,
          count: said[entry.key] ?? count?.toLocaleString('en-US') ?? '…',
          hint: entry.hint,
          tooltip: `${entry.label} — ${entry.hint}`,
          share: count !== null && all && entry.key !== 'all' ? count / all : undefined,
          data: { 'data-card': entry.key },
          countData: { 'data-count': said[entry.key] ?? count?.toString() ?? '' }
        };
      })}
    />
  );
}

/** The view cards as the compact bar's menu (`CompactMenu`'s items): each card's link, words and
 *  count as `ViewCards` draws them. */
export function viewCardItems(
  cards: readonly IRecordCard[],
  counted: Nullable<IRecordCounts>,
  params: URLSearchParams,
  said: Readonly<Record<string, string>> = {}
): ICompactMenuItem[] {
  const card = params.get('card') ?? 'all';
  return cards.map((entry) => {
    const next = dropPaging(new URLSearchParams(params));
    if (entry.key === 'all') next.delete('card');
    else next.set('card', entry.key);
    return {
      key: entry.key,
      label: entry.label,
      to: { search: next.toString() },
      current: card === entry.key,
      count: said[entry.key] ?? counted?.cards[entry.key]?.count.toLocaleString('en-US') ?? '…'
    };
  });
}

/** The search box. It reads as typed: the address follows the draft, trimmed, a quarter-second
 *  after the last key, and Enter sends it at once. The draft is held here, so a key re-renders
 *  the box and not the page's rows. */
export function RecordSearch({
  q,
  label,
  placeholder,
  onSearch,
  compact = false
}: {
  q: string;
  label: string;
  placeholder: string;
  onSearch: (text: string) => void;
  /** The compact bar's: narrower. */
  compact?: boolean;
}) {
  const { draft, onDraft: setDraft, onSubmit } = useQueueSearch(q, onSearch);

  if (compact) {
    return (
      <CompactSearch
        label={label}
        placeholder={placeholder}
        draft={draft}
        onDraft={setDraft}
        onSubmit={onSubmit}
      />
    );
  }
  return (
    <form
      role="search"
      className="w-full min-w-48 sm:w-auto sm:max-w-96 sm:flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FilterBox
        label={label}
        placeholder={placeholder}
        value={draft}
        onChange={setDraft}
        className="block"
        inputClassName="h-9"
      />
    </form>
  );
}

/**
 * One funnel parameter's values, one choice: "Any", then the values among the rows the other
 * filters keep with their counts, most common first — read as the funnel opens and, for a cycle,
 * as its box is typed in. The value chosen stays listed. Where the values cannot be read the
 * funnel offers `known`, uncounted, so the filter works without them.
 */
export function FacetChoice({
  label,
  heading,
  typed,
  base,
  read,
  value,
  known,
  onChoose
}: {
  label: string;
  /** Said above the values — where one funnel holds more than one parameter. */
  heading: boolean;
  /** A box narrowing the values by a prefix. */
  typed: boolean;
  /** The field and the query the values are read under. */
  base: string;
  read: (prefix: string, signal: AbortSignal) => Promise<IRecordFacets>;
  value: Nullable<string>;
  known: readonly IFilterOption[];
  onChoose: (value: Nullable<string>) => void;
}) {
  const [prefix, setPrefix] = React.useState('');
  // Each read is held with what it answers, `facets` null when it failed: the values of another
  // query are never offered, and a new prefix keeps the last ones while it is read.
  const [held, setHeld] = React.useState<Nullable<IFacetRead>>(null);
  const asked = JSON.stringify([base, prefix.trim()]);
  const current = held?.base === base ? held : null;
  const facets = current?.facets ?? null;
  const unread = current?.asked === asked && current.facets === null;
  const latest = React.useRef(read);
  latest.current = read;

  React.useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      latest
        .current(prefix.trim(), controller.signal)
        .then((answer) => setHeld({ base, asked, facets: answer }))
        .catch(() => {
          if (!controller.signal.aborted) setHeld({ base, asked, facets: null });
        });
    }, typed ? 200 : 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `asked` spells the query and the prefix.
  }, [asked]);

  const labelOf = (code: string) => optionLabel(known, code) ?? asSpelled(code);
  const offered: { value: string; count?: number }[] = (
    facets?.values ?? (unread ? known.map((option) => ({ value: option.value })) : [])
  ).filter((facet) => facet.value !== value);
  const name = React.useId();
  const radio = (code: Nullable<string>, words: string, count?: number) => (
    <label key={code ?? ''} className="flex items-center gap-2">
      <input
        type="radio"
        name={name}
        className="accent-[var(--color-sapphire)]"
        checked={code === value}
        onChange={() => onChoose(code)}
      />
      <span className="flex-1 truncate">{words}</span>
      {count !== undefined && (
        <span className="text-xs tabular-nums text-muted-foreground">
          {count.toLocaleString('en-US')}
        </span>
      )}
    </label>
  );

  return (
    <div className="grid gap-2">
      {heading && <p className="font-medium">{label}</p>}
      {typed && (
        <Input
          aria-label={`Find ${label.toLowerCase()}`}
          placeholder="Find a cycle"
          value={prefix}
          onChange={(event) => setPrefix(event.target.value)}
          className="h-8"
        />
      )}
      <fieldset aria-label={label} className="grid max-h-72 gap-1.5 overflow-y-auto">
        {radio(null, 'Any')}
        {value !== null && radio(value, labelOf(value))}
        {offered.map((facet) => radio(facet.value, labelOf(facet.value), facet.count))}
        {facets === null && !unread && (
          <p className="text-xs text-muted-foreground">Reading the values…</p>
        )}
        {(facets !== null || unread) && offered.length === 0 && value === null && (
          <p className="text-xs text-muted-foreground">No values.</p>
        )}
      </fieldset>
    </div>
  );
}

interface IFacetRead {
  base: string;
  /** `base` and the prefix. */
  asked: string;
  facets: Nullable<IRecordFacets>;
}

/** A sort key's order in words. */
function orderWords(key: RecordListSortKey, dir: 'asc' | 'desc'): string {
  if (key === 'name') return dir === 'asc' ? 'A to Z' : 'Z to A';
  return dir === 'desc' ? 'newest first' : 'oldest first';
}

/** A page of results, with the query it answers and what narrowed that query. */
interface IAnswered<Row> {
  asked: string;
  /** The search the page answers — whose words the rows' matches mark. */
  q: string;
  searched: boolean;
  filtered: boolean;
  card: Nullable<string>;
  page: IPage<Row>;
}

/** Says what narrowed the list, so "none matches" is never read as "none is held". */
function emptyStatement(
  one: string,
  searched: boolean,
  filtered: boolean,
  card: Nullable<string>
): string {
  const No = card === null ? `No ${one}` : `No ${one} in ${card}`;
  if (searched && filtered) return `${No} matches this search with these filters.`;
  if (searched) return `${No} matches this search.`;
  if (filtered) return `${No} matches these filters.`;
  return card === null ? `${No} is held yet.` : `${No}.`;
}
